import { PDFDocument } from 'pdf-lib'
import { MAX_OUTPUT_BYTES, MAX_OUTPUT_PIXELS, MAX_OUTPUT_SIDE, rotatedCropSize, sourceCropForRotation, type PageTransform } from './lib'

export interface ProcessorPage {
  file: File
  width: number
  height: number
  transform: PageTransform
}

export interface BitmapResource {
  width: number
  height: number
  close: () => void
}

export interface PixelBuffer {
  data: Uint8ClampedArray
}

export interface CanvasContextResource {
  fillStyle: string
  filter: string
  fillRect: (x: number, y: number, width: number, height: number) => void
  translate: (x: number, y: number) => void
  rotate: (angle: number) => void
  drawImage: (image: BitmapResource, sx: number, sy: number, sw: number, sh: number, dx: number, dy: number, dw: number, dh: number) => void
  resetTransform: () => void
  getImageData: (sx: number, sy: number, sw: number, sh: number) => PixelBuffer
  putImageData: (data: PixelBuffer, dx: number, dy: number) => void
}

export interface CanvasResource {
  getContext: () => CanvasContextResource | null
  convertToBlob: (options: { type: string; quality: number }) => Promise<Blob>
}

export interface ScannerProcessorDeps {
  createBitmap: (file: File) => Promise<BitmapResource>
  createCanvas: (width: number, height: number) => CanvasResource
}

async function canvasJpeg(canvas: CanvasResource): Promise<Blob> {
  const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.9 })
  if (blob.type !== 'image/jpeg') throw new Error('Unexpected encoder output')
  return blob
}

async function renderPage(page: ProcessorPage, deps: ScannerProcessorDeps): Promise<{ blob: Blob; pixels: number }> {
  const bitmap = await deps.createBitmap(page.file)
  try {
    const crop = sourceCropForRotation(page.transform.crop, page.transform.rotation)
    const sx = Math.round(bitmap.width * crop.left / 100)
    const sy = Math.round(bitmap.height * crop.top / 100)
    const sw = Math.max(1, Math.round(bitmap.width * (1 - crop.left / 100 - crop.right / 100)))
    const sh = Math.max(1, Math.round(bitmap.height * (1 - crop.top / 100 - crop.bottom / 100)))
    const rotated = rotatedCropSize(bitmap.width, bitmap.height, page.transform)
    const scale = Math.min(1, MAX_OUTPUT_SIDE / Math.max(rotated.width, rotated.height))
    const outputWidth = Math.max(1, Math.round(rotated.width * scale))
    const outputHeight = Math.max(1, Math.round(rotated.height * scale))
    const canvas = deps.createCanvas(outputWidth, outputHeight)
    const context = canvas.getContext()
    if (!context) throw new Error('Canvas unavailable')
    context.fillStyle = '#fff'
    context.fillRect(0, 0, outputWidth, outputHeight)
    context.translate(outputWidth / 2, outputHeight / 2)
    context.rotate(page.transform.rotation * Math.PI / 180)
    const drawWidth = page.transform.rotation === 90 || page.transform.rotation === 270 ? outputHeight : outputWidth
    const drawHeight = page.transform.rotation === 90 || page.transform.rotation === 270 ? outputWidth : outputHeight
    context.filter = `${page.transform.mode === 'color' ? '' : 'grayscale(1) '}brightness(${100 + page.transform.brightness}%) contrast(${100 + page.transform.contrast}%)`
    context.drawImage(bitmap, sx, sy, sw, sh, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight)
    if (page.transform.mode === 'black-white') {
      context.resetTransform()
      context.filter = 'none'
      const image = context.getImageData(0, 0, outputWidth, outputHeight)
      for (let index = 0; index < image.data.length; index += 4) {
        const value = (image.data[index] ?? 0) >= 128 ? 255 : 0
        image.data[index] = value
        image.data[index + 1] = value
        image.data[index + 2] = value
      }
      context.putImageData(image, 0, 0)
    }
    return { blob: await canvasJpeg(canvas), pixels: outputWidth * outputHeight }
  } finally {
    bitmap.close()
  }
}

export class OutputPixelBudgetError extends Error {
  constructor() {
    super('Output pixel budget exceeded')
    this.name = 'OutputPixelBudgetError'
  }
}

function estimatedOutputPixels(page: ProcessorPage): number {
  const rotated = rotatedCropSize(page.width, page.height, page.transform)
  const scale = Math.min(1, MAX_OUTPUT_SIDE / Math.max(rotated.width, rotated.height))
  return Math.max(1, Math.round(rotated.width * scale)) * Math.max(1, Math.round(rotated.height * scale))
}

export async function processScannerPages(pages: ProcessorPage[], deps: ScannerProcessorDeps): Promise<Uint8Array> {
  const expectedPixels = pages.reduce((sum, page) => sum + estimatedOutputPixels(page), 0)
  if (expectedPixels > MAX_OUTPUT_PIXELS) throw new OutputPixelBudgetError()
  const pdf = await PDFDocument.create()
  let outputPixels = 0
  let outputBytes = 0
  for (const page of pages) {
    const rendered = await renderPage(page, deps)
    outputPixels += rendered.pixels
    if (outputPixels > MAX_OUTPUT_PIXELS) throw new OutputPixelBudgetError()
    const bytes = new Uint8Array(await rendered.blob.arrayBuffer())
    outputBytes += bytes.byteLength
    if (outputBytes > MAX_OUTPUT_BYTES) throw new Error('Output byte budget exceeded')
    const image = await pdf.embedJpg(bytes)
    const pdfPage = pdf.addPage([image.width, image.height])
    pdfPage.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height })
  }
  return await pdf.save()
}
